/**
 * Which hand played each note of a keyboard track, so that it can be written on two staves.
 *
 * A MIDI track from a keyboard is one list of notes, and nothing in it says which hand a
 * note was under. What does say it, most of the time, is what a hand is: it covers about an
 * octave, it stays where it is rather than jumping, the left one is below the right one and
 * the two meet somewhere near middle C. So the track is read moment by moment, each moment
 * being a point where a key goes down, and at each one a line is drawn between two keys:
 * what sounds below it is the left hand's, what sounds at or above it the right's. Where the
 * line goes is whatever costs least over the whole track -- a hand stretched past an octave
 * costs, a line that moves costs, a line far from middle C costs a little -- which is found
 * exactly, by dynamic programming over the moments, rather than greedily.
 *
 * The left hand is let wider than the right. A bass note and the chord over it are written
 * as one hand's though they are a tenth or two apart, because the pedal holds the one while
 * the hand goes to the other; a right hand that wide is nearly always two hands.
 *
 * It is a line and not a set, so hands that cross are written as if they had not: the notes
 * come out on the staff they sound on, which is the staff they are easiest to read from.
 */

import { PPQ } from "../document/duration";
import type { ScaledNote } from "./spans";

/** A track's notes under two hands. */
export interface Hands {
  readonly right: readonly ScaledNote[];
  readonly left: readonly ScaledNote[];
}

/** The lowest and highest key the line between the hands is tried at. */
const LOWEST = 36;
const HIGHEST = 84;
const MIDDLE_C = 60;
/** What one hand covers without stretching, and the most it reaches, in semitones. */
const OCTAVE = 12;
const TENTH = 16;
/** A semitone of stretch past the octave, and past the tenth, where a hand cannot follow. */
const STRETCH = 1;
const BEYOND = 6;
/** A semitone past the octave under the left hand, however far: the pedal holds its bass. */
const LEFT = 0.5;
/** The line moved one semitone between two moments a beat or less apart. */
const MOVE = 0.5;
/** The line one semitone from middle C. */
const AWAY = 0.05;
/** The least share of the notes a hand must play for it to be given a staff. */
const A_HAND = 1 / 20;
/** The share of moments sounding wider than an octave from which a track takes two hands. */
const TWO_HANDED = 1 / 4;

/** General MIDI programs played from a keyboard with two hands, or a harp's two. */
const PIANOS = 8;
const CELESTA = 8;
const ORGANS: readonly [number, number] = [16, 20];
const HARP = 46;

/**
 * Whether a track with this program is read from two staves at all.
 *
 * A track that states no program is one: General MIDI plays it as a piano. A guitar or a
 * violin is not, however wide its chords.
 */
export function keyboard(program: number | null): boolean {
  if (program === null) return true;
  if (program < PIANOS || program === CELESTA || program === HARP) return true;
  return program >= ORGANS[0] && program <= ORGANS[1];
}

/** One moment a key goes down at: when, and every key sounding then, low to high. */
interface Moment {
  readonly at: number;
  readonly keys: readonly number[];
}

function momentsOf(notes: readonly ScaledNote[]): Moment[] {
  const ordered = [...notes].sort((first, second) => first.start - second.start);
  const moments: Moment[] = [];
  let sounding: ScaledNote[] = [];
  let index = 0;
  while (index < ordered.length) {
    const at = (ordered[index] as ScaledNote).start;
    sounding = sounding.filter((note) => note.start + note.ticks > at);
    while (index < ordered.length && (ordered[index] as ScaledNote).start === at) {
      sounding.push(ordered[index] as ScaledNote);
      index += 1;
    }
    moments.push({ at, keys: sounding.map((note) => note.key).sort((a, b) => a - b) });
  }
  return moments;
}

function stretch(lowest: number, highest: number): number {
  const span = highest - lowest;
  if (span <= OCTAVE) return 0;
  if (span <= TENTH) return (span - OCTAVE) * STRETCH;
  return (TENTH - OCTAVE) * STRETCH + (span - TENTH) * BEYOND;
}

/** What it costs to draw the line under `line` at a moment sounding `keys`. */
function costAt(keys: readonly number[], line: number): number {
  const below = keys.filter((key) => key < line);
  const above = keys.length - below.length;
  let cost = Math.abs(line - MIDDLE_C) * AWAY;
  if (below.length > 0) {
    const span = (below[below.length - 1] as number) - (below[0] as number);
    cost += Math.max(0, span - OCTAVE) * LEFT;
  }
  if (above > 0) cost += stretch(keys[below.length] as number, keys[keys.length - 1] as number);
  return cost;
}

/** Where the line between the hands runs at each moment, at the least cost over them all. */
function linesOf(moments: readonly Moment[]): number[] {
  const width = HIGHEST - LOWEST + 1;
  const from: Int8Array[] = [];
  let best = new Float64Array(width);
  moments.forEach((moment, index) => {
    const next = new Float64Array(width);
    const back = new Int8Array(width);
    const before = moments[index - 1];
    // A hand has time to move in a rest: a beat of it halves the cost, two a third.
    const beats = before === undefined ? 0 : (moment.at - before.at) / PPQ;
    const move = MOVE / Math.max(1, beats);
    for (let line = 0; line < width; line += 1) {
      let least = Infinity;
      let chosen = line;
      if (before === undefined) least = 0;
      else {
        for (let other = 0; other < width; other += 1) {
          const cost = (best[other] as number) + Math.abs(line - other) * move;
          if (cost < least) {
            least = cost;
            chosen = other;
          }
        }
      }
      next[line] = least + costAt(moment.keys, LOWEST + line);
      back[line] = chosen;
    }
    from.push(back);
    best = next;
  });

  let line = 0;
  for (let other = 1; other < width; other += 1) {
    if ((best[other] as number) < (best[line] as number)) line = other;
  }
  const lines = new Array<number>(moments.length).fill(MIDDLE_C);
  for (let index = moments.length - 1; index >= 0; index -= 1) {
    lines[index] = LOWEST + line;
    line = (from[index] as Int8Array)[line] as number;
  }
  return lines;
}

/**
 * The track under two hands, or nothing where one staff holds it.
 *
 * One staff holds it when its notes seldom sound wider than an octave together -- a tune,
 * or a single line however far it ranges -- and when one of the hands found would play next
 * to nothing, since a staff drawn for a handful of notes is an empty staff on every page.
 */
export function handsOf(notes: readonly ScaledNote[]): Hands | null {
  const moments = momentsOf(notes);
  const wide = moments.filter(
    (moment) =>
      (moment.keys[moment.keys.length - 1] as number) - (moment.keys[0] as number) > OCTAVE,
  );
  if (moments.length === 0 || wide.length < moments.length * TWO_HANDED) return null;

  const lines = linesOf(moments);
  const lineAt = new Map(moments.map((moment, index) => [moment.at, lines[index] as number]));
  const right = notes.filter((note) => note.key >= (lineAt.get(note.start) ?? MIDDLE_C));
  const left = notes.filter((note) => note.key < (lineAt.get(note.start) ?? MIDDLE_C));
  const least = notes.length * A_HAND;
  return right.length < least || left.length < least ? null : { right, left };
}
